/**
 * Inspector "Tunnels" panel — renders the per-session, per-host tunnel
 * health chip + a reconnect button. The body is plain (no Lit element)
 * so it composes with the existing `renderFleetInspectorPanel` shell
 * without further wiring.
 *
 * The data contract is the same `TunnelRecord[]` shape produced by
 * `TunnelManager.list()` — keyed by `hostId` because the active
 * session can target multiple hosts in the future, and filtered by the
 * active session in `tunnel-health.ts`.
 *
 * Composer hygiene: this panel MUST never produce a string that the
 * caller could route into the chat composer. The only side effect is
 * `onReconnect`, which calls `chat.abort` and restarts the gateway
 * client via the controller.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { TunnelRecord } from "../tunnel/tunnel-state.ts";

export type TunnelHealthPanelProps = {
  records: TunnelRecord[];
  activeSessionKey: string;
  onReconnect: (key: { sessionId: string; hostId: string }) => void;
  onForget?: (key: { sessionId: string; hostId: string }) => void;
};

const STATE_LABEL: Record<TunnelRecord["state"], string> = {
  connecting: "Connecting",
  healthy: "Healthy",
  degraded: "Degraded",
  dead: "Dead",
};

/**
 * Returns the CSS class for a tunnel state. The chat-composer pill
 * uses a different class so an inspector reader scanning the page can
 * tell "tunnel chip, not the chat-status pill".
 */
export function stateClass(state: TunnelRecord["state"]): string {
  return `tunnel-chip tunnel-chip--${state}`;
}

function relativeAge(ms: number | null, now: number): string {
  if (ms == null) {
    return "never";
  }
  const delta = Math.max(0, now - ms);
  if (delta < 1_000) {
    return "just now";
  }
  if (delta < 60_000) {
    return `${Math.round(delta / 1_000)}s ago`;
  }
  if (delta < 3_600_000) {
    return `${Math.round(delta / 60_000)}m ago`;
  }
  return `${Math.round(delta / 3_600_000)}h ago`;
}

export function renderTunnelHealthPanel(props: TunnelHealthPanelProps): TemplateResult {
  const records = props.records;
  if (records.length === 0) {
    return html`
      <div class="tunnel-panel">
        <p class="muted">No tunnel registered for this session yet.</p>
        <p class="muted tunnel-panel__hint">
          The chip appears the moment the gateway WebSocket starts connecting. If
          it never shows, check the Settings dialog for the gateway URL.
        </p>
      </div>
    `;
  }
  const now = Date.now();
  return html`
    <div class="tunnel-panel" role="region" aria-label="Tunnel health">
      <ul class="tunnel-panel__list">
        ${records.map(
          (rec) => html`
            <li class="tunnel-panel__row" data-state=${rec.state}>
              <div class="tunnel-panel__left">
                <span class=${stateClass(rec.state)} title=${STATE_LABEL[rec.state]}>
                  ${STATE_LABEL[rec.state]}
                </span>
                <div class="tunnel-panel__ids">
                  <div class="tunnel-panel__id">
                    <span class="tunnel-panel__id-label">session</span>
                    <code>${rec.key.sessionId}</code>
                  </div>
                  <div class="tunnel-panel__id">
                    <span class="tunnel-panel__id-label">host</span>
                    <code>${rec.key.hostId}</code>
                  </div>
                </div>
              </div>
              <div class="tunnel-panel__right">
                <div class="tunnel-panel__meta">
                  <span>last healthy: ${relativeAge(rec.lastHealthyAt, now)}</span>
                  <span>auto-retries: ${rec.autoReconnectAttempts}</span>
                  ${rec.lastError
                    ? html`<span class="tunnel-panel__error">${rec.lastError}</span>`
                    : nothing}
                </div>
                <div class="tunnel-panel__actions">
                  <button
                    type="button"
                    class="tunnel-panel__reconnect"
                    @click=${() => props.onReconnect({ ...rec.key })}
                    ?disabled=${rec.state === "connecting"}
                  >
                    ${rec.state === "dead" ? "Reconnect" : "Restart"}
                  </button>
                  ${props.onForget
                    ? html`
                        <button
                          type="button"
                          class="tunnel-panel__forget"
                          @click=${() => props.onForget?.({ ...rec.key })}
                        >
                          Forget
                        </button>
                      `
                    : nothing}
                </div>
              </div>
            </li>
          `,
        )}
      </ul>
    </div>
  `;
}
